import * as React from "react";
import {
  Card,
  Subtitle1,
  Subtitle2,
  Text,
  Input,
  Textarea,
  Select,
  Button,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  Delete20Regular,
  Edit20Regular,
  Save20Regular,
  Dismiss20Regular,
  ArrowSync20Regular,
  Image20Regular,
} from "@fluentui/react-icons";
import {
  type Announcement,
  type AnnouncementInput,
  type AnnouncementTargetType,
  announcementService,
} from "../../services/announcementService";
import { useReferenceData } from "../../context/ReferenceDataContext";
import { useResponsive } from "../../hooks/useResponsive";
import { AuthedImage } from "../../components/common/AuthedImage";
import { useBestaetigen } from "../../components/redesign/bestaetigen";

const deDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

type FormState = {
  title: string;
  body: string;
  images: string[];
  targetType: AnnouncementTargetType;
  targetTeamId: string;
};

const emptyForm: FormState = {
  title: "",
  body: "",
  images: [],
  targetType: "TENANT",
  targetTeamId: "",
};

/**
 * „Courté Intern" / News-Pflege in den Einstellungen (DD4) — nur admin/gfgl.
 * Liste vorhandener News + Anlege-/Bearbeiten-Formular (Titel, Text, Bild-Upload,
 * Ziel TENANT/TEAM mit Team-Auswahl). Responsive (einspaltig auf schmalen Screens).
 */
export const NewsSection: React.FC = () => {
  const { bestaetigen } = useBestaetigen();
  const { isPhone } = useResponsive();
  const { appTeams } = useReferenceData();

  const [rows, setRows] = React.useState<Announcement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [editId, setEditId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const toasterId = useId("news-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = (title: string, body: string, intent: "success" | "error") =>
    dispatchToast(
      <Toast>
        <ToastTitle>{title}</ToastTitle>
        <ToastBody>{body}</ToastBody>
      </Toast>,
      { intent },
    );

  const teamName = React.useCallback(
    (id?: string | null): string =>
      (id && appTeams.find((t) => t.id === id)?.name) || "Team",
    [appTeams],
  );

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await announcementService.listAll());
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden der News.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
  };

  const startEdit = (a: Announcement) => {
    setEditId(a.id);
    setForm({
      title: a.title,
      body: a.body ?? "",
      images: Array.isArray(a.images) ? a.images : [],
      targetType: a.targetType === "TEAM" ? "TEAM" : "TENANT",
      targetTeamId: a.targetTeamId ?? "",
    });
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onPickFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      for (const file of Array.from(files)) {
        uploaded.push(await announcementService.uploadImage(file));
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e: any) {
      notify("Upload fehlgeschlagen", e?.message || "Bild konnte nicht hochgeladen werden.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (ref: string) =>
    setForm((f) => ({ ...f, images: f.images.filter((r) => r !== ref) }));

  const canSubmit =
    form.title.trim().length > 0 &&
    (form.targetType === "TENANT" || form.targetTeamId.trim().length > 0) &&
    !saving &&
    !uploading;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const payload: AnnouncementInput = {
      title: form.title.trim(),
      body: form.body,
      images: form.images.length > 0 ? form.images : null,
      targetType: form.targetType,
      targetTeamId: form.targetType === "TEAM" ? form.targetTeamId : null,
    };
    try {
      if (editId) {
        await announcementService.update(editId, payload);
        notify("Gespeichert", "Die Mitteilung wurde aktualisiert.", "success");
      } else {
        await announcementService.create(payload);
        notify("Veröffentlicht", "Die Mitteilung wurde angelegt.", "success");
      }
      resetForm();
      await loadAll();
    } catch (e: any) {
      notify("Fehler", e?.message || "Speichern fehlgeschlagen.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a: Announcement) => {
    const ok = await bestaetigen({
      titel: "Mitteilung löschen?",
      text: `„${a.title}" wandert in den Papierkorb und lässt sich dort wiederherstellen.`,
      ja: "Löschen",
      ton: "danger",
    });
    if (!ok) return;
    setDeletingId(a.id);
    try {
      await announcementService.remove(a.id);
      if (editId === a.id) resetForm();
      await loadAll();
      notify("Gelöscht", "Die Mitteilung wurde in den Papierkorb verschoben.", "success");
    } catch (e: any) {
      notify("Fehler", e?.message || "Löschen fehlgeschlagen.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: isPhone ? 16 : 20 }}>
      <Toaster toasterId={toasterId} position="top-end" />

      {/* Anlege-/Bearbeiten-Formular */}
      <Card style={{ padding: isPhone ? 16 : 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <Subtitle1>{editId ? "Mitteilung bearbeiten" : "Neue Mitteilung"}</Subtitle1>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Text weight="semibold">Titel</Text>
          <Input
            value={form.title}
            onChange={(_, d) => setForm((f) => ({ ...f, title: d.value }))}
            placeholder="Überschrift der Mitteilung"
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Text weight="semibold">Text</Text>
          <Textarea
            value={form.body}
            onChange={(_, d) => setForm((f) => ({ ...f, body: d.value }))}
            placeholder="Inhalt der Mitteilung"
            resize="vertical"
            rows={5}
          />
        </div>

        {/* Bild-Upload */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Text weight="semibold">Bilder</Text>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => void onPickFiles(e.target.files)}
          />
          <div>
            <Button
              appearance="secondary"
              icon={uploading ? <Spinner size="tiny" /> : <Image20Regular />}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Lade hoch…" : "Bild hinzufügen"}
            </Button>
          </div>
          {form.images.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {form.images.map((ref) => (
                <div
                  key={ref}
                  style={{
                    position: "relative",
                    width: 96,
                    height: 96,
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <AuthedImage
                    src={ref}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<Dismiss20Regular />}
                    aria-label="Bild entfernen"
                    onClick={() => removeImage(ref)}
                    style={{
                      position: "absolute",
                      top: 2,
                      right: 2,
                      minWidth: "auto",
                      background: "rgba(255,255,255,0.85)",
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ziel */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Text weight="semibold">Ziel</Text>
            <Select
              value={form.targetType}
              onChange={(_, d) =>
                setForm((f) => ({
                  ...f,
                  targetType: d.value === "TEAM" ? "TEAM" : "TENANT",
                }))
              }
            >
              <option value="TENANT">Alle (Mandant)</option>
              <option value="TEAM">Bestimmtes Team</option>
            </Select>
          </div>
          {form.targetType === "TEAM" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Text weight="semibold">Team</Text>
              <Select
                value={form.targetTeamId}
                onChange={(_, d) => setForm((f) => ({ ...f, targetTeamId: d.value }))}
              >
                <option value="">— Team wählen —</option>
                {appTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button
            appearance="primary"
            icon={saving ? <Spinner size="tiny" /> : editId ? <Save20Regular /> : <Add20Regular />}
            disabled={!canSubmit}
            onClick={() => void submit()}
          >
            {editId ? "Speichern" : "Veröffentlichen"}
          </Button>
          {editId && (
            <Button appearance="secondary" icon={<Dismiss20Regular />} onClick={resetForm}>
              Abbrechen
            </Button>
          )}
        </div>
      </Card>

      {/* Liste */}
      <Card style={{ padding: isPhone ? 16 : 24, display: "flex", flexDirection: "column", gap: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Subtitle1>Vorhandene Mitteilungen</Subtitle1>
          <Button
            appearance="secondary"
            icon={<ArrowSync20Regular />}
            onClick={() => void loadAll()}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </div>

        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {loading ? (
          <Spinner label="Lade Mitteilungen…" />
        ) : rows.length === 0 ? (
          <Text>Noch keine Mitteilungen vorhanden.</Text>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((a) => (
              <div
                key={a.id}
                style={{
                  display: "flex",
                  flexDirection: isPhone ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isPhone ? "stretch" : "center",
                  gap: 8,
                  padding: "10px 12px",
                  border: "1px solid #ececec",
                  borderRadius: 8,
                  background: editId === a.id ? "#faf6fa" : undefined,
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <Subtitle2 style={{ wordBreak: "break-word" }}>{a.title}</Subtitle2>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <Badge appearance="tint" color={a.targetType === "TEAM" ? "warning" : "brand"}>
                      {a.targetType === "TEAM" ? teamName(a.targetTeamId) : "Alle"}
                    </Badge>
                    <Text size={200}>{deDate(a.createdAt)}</Text>
                    {Array.isArray(a.images) && a.images.length > 0 && (
                      <Text size={200}>
                        {a.images.length} {a.images.length === 1 ? "Bild" : "Bilder"}
                      </Text>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <Button
                    appearance="secondary"
                    icon={<Edit20Regular />}
                    onClick={() => startEdit(a)}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    appearance="secondary"
                    icon={deletingId === a.id ? <Spinner size="tiny" /> : <Delete20Regular />}
                    disabled={deletingId === a.id}
                    onClick={() => void remove(a)}
                  >
                    Löschen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default NewsSection;
